import { useCallback, useState } from "react";
import { Box, CircularProgress, InputAdornment, TextField, Typography } from "@mui/material";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import api from "../../services/api";
import { colors, formatTime } from "./constants";

// NOTE: slots-availability endpoint path/params are still a best guess —
// confirm the real endpoint shape once the backend's makeup-scheduling
// API is finalized. service_id is now confirmed as available on the row.
export default function MakeupScheduleFields({
  branchId,
  serviceId,
  makeupDate,
  selectedSlot,
  onDateChange,
  onSlotChange,
}) {
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const getAvailableSlots = useCallback(
    async (date) => {
      if (!date) {
        setAvailableSlots([]);
        return;
      }
      setLoadingSlots(true);
      try {
        const response = await api.get("/app/patient/therapy/slots/available", {
          params: {
            branch_id: branchId,
            date,
            service_id: serviceId,
          },
        });
        setAvailableSlots(response?.data || []);
      } catch (error) {
        console.error("Error loading available slots:", error);
        setAvailableSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    },
    [branchId, serviceId],
  );

  const handleDateChange = (event) => {
    const date = event.target.value;
    onDateChange(date);
    onSlotChange(null);
    getAvailableSlots(date);
  };

  return (
    <Box
      sx={{
        p: 2,
        mb: 2.5,
        borderRadius: 3,
        backgroundColor: colors.blueLight,
        border: "1px solid #D8E5FA",
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1.5 }}>
        Schedule Makeup Session
      </Typography>

      <TextField
        fullWidth
        size="small"
        type="date"
        label="Makeup Date"
        value={makeupDate}
        onChange={handleDateChange}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <CalendarMonthRoundedIcon sx={{ fontSize: 18, color: colors.secondary }} />
            </InputAdornment>
          ),
        }}
      />

      {makeupDate && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: colors.secondary, mb: 1 }}>
            Available Slots
          </Typography>

          {loadingSlots ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : availableSlots.length === 0 ? (
            <Box
              sx={{
                p: 2,
                textAlign: "center",
                backgroundColor: "#fff",
                borderRadius: 2,
                border: `1px dashed ${colors.border}`,
              }}
            >
              <Typography sx={{ fontSize: 12, color: colors.secondary }}>
                No available slots for this date.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1 }}>
              {availableSlots.map((slot) => {
                // Supports either an "id" or "service_slot_id" key, and a
                // few possible capacity field names, since the real
                // response shape isn't confirmed yet.
                const slotId = slot.id ?? slot.service_slot_id;
                const isSelected = Number(selectedSlot) === Number(slotId);
                const available = Number(
                  slot.available_capacity ??
                    slot.available_slots ??
                    slot.remaining_capacity ??
                    slot.capacity ??
                    0,
                );
                const isFull = available <= 0;

                return (
                  <Box
                    key={slotId}
                    onClick={() => {
                      if (!isFull) onSlotChange(slotId);
                    }}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: isSelected
                        ? `2px solid ${colors.primary}`
                        : `1px solid ${colors.border}`,
                      backgroundColor: isSelected ? colors.primaryLight : "#fff",
                      cursor: isFull ? "not-allowed" : "pointer",
                      opacity: isFull ? 0.5 : 1,
                      transition: "all .15s",
                      "&:hover": !isFull ? { borderColor: colors.primary } : {},
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <AccessTimeRoundedIcon
                        sx={{
                          fontSize: 17,
                          color: isSelected ? colors.primary : colors.secondary,
                        }}
                      />
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                        {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{ fontSize: 11, mt: 0.5, color: isFull ? colors.red : colors.green }}
                    >
                      {isFull ? "Full" : `${available} seat${available === 1 ? "" : "s"} available`}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
